// Phase 41 Plan 41-07 — API client for the SLA Balíček current-month
// consumption snapshot.
//
// Shape mirrors backend `CurrentMonthConsumptionDTO` from
// `backend/src/services/projectPackageService.ts:325-336` (frozen ABI).
// Wire format verified against `backend/src/api/schemas/projectPackage.ts`
// (Pitfall 3 / fast-json-stringify): all 10 fields below appear in BOTH
// `properties` AND `required` on the backend response schema, so the wire
// will not silently strip any of them.
//
// Endpoint mounted via `backend/src/api/routes/projectPackageRoutes.ts:33`
// at GET /api/projects/:projectId/package/current-month-consumption,
// gated by `requireAdminOrProjectMemberByProject` — only admins / PMs /
// assigned project members receive 200 (T-41-08 mitigation: defense in
// depth — UI also hides the card when the request 404s/403s).

import { apiGet } from './client';

export interface PackageThreshold {
  /** Whole-number percentage stored on `ProjectPackageAlertThreshold`. */
  percentage: number;
  /** True when this month's `lastTriggeredMonth` matches the snapshot's `periodYearMonth`. */
  crossed: boolean;
}

export interface ProjectPackageCurrentMonthConsumption {
  /** UTC `YYYY-MM` derived in the service via `Date.UTC` (Landmine 4). */
  periodYearMonth: string;
  /** Configured monthly Balíček hours (null when project has no package configured). */
  balicekHours: number | null;
  /** Decimal-as-string with 2 fractional digits (e.g. "12.50"). */
  consumedHours: string;
  /** Decimal-as-string with 2 fractional digits (e.g. "35.71"). */
  percentageConsumed: string;
  /** Decimal-as-string when overage exists, null when within cap. */
  overageHours: string | null;
  /** Decimal-as-string when overage exists AND a per-role rate is known; null otherwise. */
  overageAmount: string | null;
  /** Currency code from `Project.balicekCurrency` (e.g. "CZK", "EUR", "USD"); null when unconfigured. */
  currency: string | null;
  /** Configured alert thresholds with current-month crossing state. */
  thresholds: PackageThreshold[];
  /** Decimal-as-string when an override was approved this month; null otherwise. */
  approvedOverageHours: string | null;
  /** True when overage entries are permitted past the cap (otherwise hard-cap blocks them). */
  overLimitAllowed: boolean;
  /**
   * Phase 53 (Plan 53-04 / COVERAGE-08 display) — hours carried forward from
   * prior period(s) via rollover policy. Present only when the project has a
   * non-'none' rolloverPolicy and carryover > 0. Null / absent = no carryover.
   */
  carryoverHours?: number | null;
}

export function fetchProjectPackageStatus(projectId: number) {
  return apiGet<{ data: ProjectPackageCurrentMonthConsumption }>(
    `/projects/${projectId}/package/current-month-consumption`,
  );
}
